import React, { useMemo } from 'react';
import { Search, Building2 } from 'lucide-react';

const ChatAdminSelector = ({ admins, selectedAdmin, onSelectAdmin, loading, search, onSearchChange }) => {
  const groupedCompanies = useMemo(() => {
    const groups = {};
    admins.forEach((admin) => {
      const companyId = admin.company?._id || 'unassigned';
      const companyName = admin.company?.name || 'Unassigned Admins';
      if (!groups[companyId]) {
        groups[companyId] = {
          id: companyId,
          name: companyName,
          admins: []
        };
      }
      groups[companyId].admins.push(admin);
    });
    return Object.values(groups).sort((a, b) => {
      if (a.id === 'unassigned') return 1;
      if (b.id === 'unassigned') return -1;
      return a.name.localeCompare(b.name);
    });
  }, [admins]);

  return (
    <div className="flex flex-col h-full bg-white border-r border-slate-100">
      {/* Search Header */}
      <div className="p-4 border-b border-slate-100 bg-white/50">
        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Company Admins</h4>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Filter admin or company..."
            className="w-full pl-9 pr-3 py-1.5 rounded-xl bg-white border border-slate-100 text-xs text-slate-700 placeholder-slate-500 focus:outline-none focus:border-green-600 transition"
          />
        </div>
      </div>

      {/* Admin List */}
      <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
        {loading ? (
          <div className="p-6 text-center text-xs text-slate-500">Loading admins...</div>
        ) : admins.length === 0 ? (
          <div className="p-6 text-center text-xs text-slate-500">No admins found</div>
        ) : (
          groupedCompanies.map((company) => (
            <div key={company.id} className="flex flex-col">
              {/* Company Header */}
              <div className="px-4 py-2 bg-white/30 flex items-center space-x-2 border-y border-slate-100/40">
                <Building2 className="w-3.5 h-3.5 text-green-700" />
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider truncate">
                  {company.name}
                </span>
                <span className="text-[9px] px-1.5 py-0.2 bg-slate-100 text-slate-500 rounded-full font-semibold ml-auto">
                  {company.admins.length}
                </span>
              </div>
              
              {/* Admins in Company */}
              <div className="divide-y divide-slate-850">
                {company.admins.map((admin) => {
                  const isSelected = selectedAdmin && selectedAdmin._id === admin._id;
                  return (
                    <button
                      key={admin._id}
                      onClick={() => onSelectAdmin(admin)}
                      className={`w-full p-3 flex items-center justify-between text-left transition ${
                        isSelected
                          ? 'bg-green-700/15 border-l-4 border-green-600'
                          : 'hover:bg-slate-50/50'
                      }`}
                    >
                      <div className="flex items-center space-x-3 truncate pl-2">
                        <div className="relative flex-shrink-0">
                          <img
                            src={admin.avatar || 'https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?w=150&auto=format&fit=crop&q=80'}
                            alt={admin.name}
                            className="w-8 h-8 rounded-full object-cover border border-slate-700"
                          />
                          <span
                            className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-slate-900 ${
                              admin.isOnline ? 'bg-emerald-500' : 'bg-slate-600'
                            }`}
                          />
                        </div>

                        <div className="truncate">
                          <p className="text-xs font-semibold text-slate-900 truncate">{admin.name}</p>
                          <p className="text-[10px] text-slate-500 truncate mt-0.5">
                            {admin.email}
                          </p>
                        </div>
                      </div>

                      {admin.unreadCount > 0 && (
                        <span className="flex-shrink-0 px-2 py-0.5 rounded-full bg-green-700 text-[10px] font-bold text-white shadow-md shadow-green-700/30">
                          {admin.unreadCount}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ChatAdminSelector;
